import { type ReactNode, useState } from "react";

import { createPortal } from "react-dom";

import { Button } from "@rook/ui";

import { EnvSwitchOverlay } from "./EnvSwitchOverlay";

/**
 * `<EnvSwitchButton>` — the button + overlay pair that drives a
 * cross-origin navigation with the CPF-NFR-028 no-visible-page-
 * change UX guarantee in one drop-in component.
 *
 * On click:
 *   1. Renders `<EnvSwitchOverlay>` (the branded full-viewport
 *      loading state)
 *   2. Waits two `requestAnimationFrame` ticks so the browser
 *      actually paints the overlay before the navigation begins
 *      (one RAF = layout commit, two = paint commit)
 *   3. Calls `window.location.assign(targetOrigin + targetPath)`
 *
 * The two-RAF guard is the difference between "customer sees the
 * branded overlay" and "customer sees a 200ms flash where the
 * overlay rendered but wasn't painted before the browser
 * unloaded". Most browsers paint reliably after two RAFs.
 *
 * Why a button + overlay together (vs separate)
 * ----------------------------------------------
 * The two have to coordinate state (clicking the button shows the
 * overlay). Bundling them removes the consumer's responsibility
 * to wire up `useState` + `onClick` + conditional render of the
 * overlay — three small things that, when each consumer does
 * them, drift over time. The composed component is the contract.
 *
 * The button is intentionally unstyled (no width, padding, color
 * defaults). Callers wrap with their own className / Tailwind /
 * style. The component's job is "do the navigation + overlay
 * correctly"; visual presentation stays the caller's call. The
 * overlay's branded styling IS the contract (CPF-NFR-028).
 */

export type EnvSwitchButtonProps = {
  /**
   * The destination origin to navigate to. MUST be in
   * `PORTAL_ORIGINS` per CPF-NFR-026. Example:
   * `"https://production.clients.portal.tryrook.io"`.
   */
  targetOrigin: string;

  /**
   * Human-readable env label rendered in the overlay. Example:
   * `"Production"`, `"Sandbox"`, `"Development"`, `"Landing"`.
   */
  targetEnvLabel: string;

  /**
   * Path to navigate to on the target origin. Defaults to `"/"`.
   * Used by the env-switcher to preserve the customer's current
   * route across env switches (e.g. they're on /home/logs in prod
   * and switch to sandbox → land on /home/logs in sandbox).
   */
  targetPath?: string;

  /**
   * Optional spinner brand color for env-color emphasis per
   * CPF-UIR-089. Defaults to ROOK brand purple (`#831DF5`); pass
   * red for prod, emerald for sandbox, blue for dev when the
   * destination env should be visually anchored in the overlay.
   */
  brandColor?: string;

  /**
   * Optional aria-label for the button. Defaults to "Switch to
   * <targetEnvLabel>". Override if the visual label and the
   * accessible label need to differ (rare).
   */
  ariaLabel?: string;

  /**
   * Optional className for the button — caller's styling escape
   * hatch. The component does NOT provide any visual defaults
   * beyond a minimal cursor-pointer + inherit type.
   */
  className?: string;

  /**
   * Optional inline style for the button. Same escape hatch as
   * className for callers that don't use a CSS framework.
   */
  style?: React.CSSProperties;

  /**
   * Button content (text, icon, or composed children). Required.
   */
  children: ReactNode;

  /**
   * Test-only escape hatch — substitute the navigation function.
   * Production code MUST NOT pass this. jsdom-based unit tests
   * pass a mock here so they can assert "navigation would have
   * happened" without actually unloading the page.
   */
  navigateFn?: (url: string) => void;
};

export const EnvSwitchButton = ({
  targetOrigin,
  targetEnvLabel,
  targetPath = "/",
  brandColor,
  ariaLabel,
  className,
  style,
  children,
  navigateFn,
}: EnvSwitchButtonProps) => {
  const [switching, setSwitching] = useState(false);

  const handleClick = (): void => {
    setSwitching(true);
    const navigate = navigateFn ?? ((url) => window.location.assign(url));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        navigate(`${targetOrigin}${targetPath}`);
      });
    });
  };

  // The overlay is `position: fixed; inset: 0` (full viewport). But when this
  // button renders inside a Radix dropdown/popover, that container sets a CSS
  // `transform` for positioning — and a transformed ancestor becomes the
  // containing block for `position: fixed`, so the overlay would be trapped
  // inside the dropdown's little box instead of covering the screen (caught
  // 2026-06-08: the "Switching…" card appeared inside the env dropdown). Portal
  // it to <body> so it escapes any transformed ancestor and is truly full-screen.
  const overlay =
    switching && typeof document !== "undefined"
      ? createPortal(
          <EnvSwitchOverlay
            targetEnvLabel={targetEnvLabel}
            brandColor={brandColor}
          />,
          document.body,
        )
      : null;

  return (
    <>
      <Button
        type="button"
        variant={null}
        size={null}
        onClick={handleClick}
        aria-label={ariaLabel ?? `Switch to ${targetEnvLabel}`}
        className={className}
        style={style}
        disabled={switching}
      >
        {children}
      </Button>
      {overlay}
    </>
  );
};
